/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { GetCBQResult } from "../api/Quiz/GetCBQResult";
import { QuizResponse } from "../api/Quiz/SubmitQuiz";
import Navbar from "../components/Layout/Navbar";
import Spinner from "../components/Shared/Spinner";
import Button from "../components/Shared/Button";
import CheckSignIcon from "../components/Icons/CheckSignIcon";
import WrongSignIcon from "../components/Icons/WrongSignIcon";
import FlagIcon from "../components/Icons/FlagIcon";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";

type Tab = "all" | "correct" | "wrong" | "flagged";
type McqTab = "wrong" | "correct" | "marked";

// Make the student's saved answer readable per question type.
const formatAnswer = (type: string, a: any): string => {
  if (!a) return "—";
  switch (type) {
    case "mcq":
      return a.answer ?? "—";
    case "multi_response":
      return (a.answers ?? []).join(", ") || "—";
    case "numeric":
      return a.value !== undefined && a.value !== null ? String(a.value) : "—";
    case "dropdown":
      return Object.values(a.blanks ?? {}).join(", ") || "—";
    case "drag_drop_ordering":
      return (
        Object.entries(a.placements ?? {})
          .map(([k, v]) => `${k} → ${v}`)
          .join(", ") || "—"
      );
    case "drag_drop_classification":
      return (
        Object.entries(a.zones ?? {})
          .map(([k, v]) => `${k}: ${(v as string[]).join(" / ")}`)
          .join("  |  ") || "—"
      );
    default:
      return JSON.stringify(a);
  }
};

// Render a dropdown/fill prompt with the student's choices filling the blanks.
const fillDropdownPrompt = (prompt: string, a: any) => {
  const blanks = (a?.blanks ?? {}) as Record<string, string>;
  return prompt.split(/(\[blank_\d+\])/g).map((part, i) => {
    const m = part.match(/^\[(blank_\d+)\]$/);
    if (m) {
      return (
        <span key={i} className="font-bold underline text-primary">
          {blanks[m[1]] || "____"}
        </span>
      );
    }
    return <span key={i}>{part}</span>;
  });
};

// The result payload stores the CORRECT answer with the per-type keys at the top
// level (dropdown: { blank_1 }, ordering: { First }, classification: { Animal }),
// whereas the student's answerJson nests them under blanks/placements/zones. Wrap
// the correct answer back into the student shape so formatAnswer/fillDropdownPrompt
// (written against the student shape) render it too. mcq/multi/numeric already
// share the same shape, so they pass through unchanged.
const normalizeCorrect = (type: string, c: any) => {
  if (!c) return c;
  switch (type) {
    case "dropdown":
      return { blanks: c };
    case "drag_drop_ordering":
      return { placements: c };
    case "drag_drop_classification":
      return { zones: c };
    default:
      return c;
  }
};

const CbqResult = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const id = sessionId || "";
  const navState = location.state as
    | { from?: string; mcqResult?: QuizResponse }
    | null;
  const backTo = navState?.from;
  // Passed from the merged MCQ+CBQ flow so both result sets show here. Absent on
  // a direct visit or a refresh, in which case only the CBQ result is shown.
  const mcqResult = navState?.mcqResult;

  const [currentTab, setCurrentTab] = useState<Tab>("all");
  const [mcqTab, setMcqTab] = useState<McqTab>("wrong");

  const { data, isLoading } = useQuery({
    queryFn: () => GetCBQResult(id),
    queryKey: ["cbq-result", id],
    enabled: !!id,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  const result = data?.data;
  const session = result?.session;

  // The CBQ counts as "taken" only when the graded payload came back with
  // scenario content. A student who fails the MCQ never unlocks the CBQ, so
  // there is no CBQ result to show — in that case only the MCQ panel renders.
  const cbqTaken =
    !!result && Array.isArray(result.scenarios) && result.scenarios.length > 0;

  // Flatten graded questions across scenarios.
  const questions = useMemo(() => {
    const out: any[] = [];
    [...(result?.scenarios ?? [])]
      .sort((a: any, b: any) => a.order - b.order)
      .forEach((sc: any, sIdx: number) => {
        [...(sc.questions ?? [])]
          .sort((a: any, b: any) => a.order - b.order)
          .forEach((q: any) =>
            out.push({ ...q, scenarioIndex: sIdx, scenarioTitle: sc.title })
          );
      });
    return out;
  }, [result]);

  const clampPercent = (n: number) => Math.min(100, Math.max(0, Math.round(n)));

  // The server grades the session and reports `totalScore` already as a
  // percentage (it arrives as a string, e.g. "20" = 20%). Question weights are
  // not uniform and partial credit exists, so the client cannot re-derive it:
  // dividing that percentage by the summed `points` — as this did — skewed
  // every exam whose weights don't total 100, reading low on a 120-point exam
  // (20 -> 17%) and above 100% on a 60-point one (80 -> 133%). Take the
  // server's number as-is; only fall back to the graded questions when the
  // session carries no score at all.
  const sessionScore = Number(session?.totalScore);
  const percentage = Number.isFinite(sessionScore)
    ? clampPercent(sessionScore)
    : (() => {
        const num = (v: any) => {
          const n = Number(v);
          return Number.isFinite(n) ? n : 0;
        };
        const totalPoints = questions.reduce((s, q) => s + num(q.points), 0);
        const earned = questions.reduce((s, q) => s + num(q.scoreEarned), 0);
        return totalPoints ? clampPercent((earned / totalPoints) * 100) : 0;
      })();
  const correctCount = questions.filter((q) => q.isCorrect).length;

  const filtered = questions.filter((q) => {
    if (currentTab === "correct") return q.isCorrect;
    if (currentTab === "wrong") return !q.isCorrect;
    if (currentTab === "flagged") return q.isFlagged;
    return true;
  });

  // MCQ questions for the active MCQ sub-tab.
  const mcqQuestions =
    mcqTab === "correct"
      ? mcqResult?.correctQuestions
      : mcqTab === "marked"
        ? mcqResult?.markedQuestions
        : mcqResult?.wrongQuestions;

  // Fused totals across both exams. Both scores are percentages (CBQ 70 = 70%),
  // so the combined percentage is their plain average. Correct/total are summed
  // as raw question counts. Only meaningful when both exams were taken.
  const hasMcq = !!mcqResult;
  const mcqTotalQ = hasMcq
    ? mcqResult!.count_correct + mcqResult!.count_wrong
    : 0;

  // One overall total across whichever exams exist. Both scores are percentages
  // (CBQ 70 = 70%): average them when both were taken, else use the one present.
  // Correct/total are summed as raw question counts. Always shown when any
  // result exists, so the header total never disappears (e.g. MCQ-only refresh).
  const showOverall = hasMcq || cbqTaken;
  // Guarded the same way as the CBQ side: a percentage that leaves 0-100 is a
  // bad input, and the header must not render an impossible total from it.
  const mcqPercent = hasMcq ? clampPercent(Number(mcqResult!.score) || 0) : 0;
  const overallPercentage = clampPercent(
    hasMcq && cbqTaken
      ? (mcqPercent + percentage) / 2
      : hasMcq
        ? mcqPercent
        : percentage
  );
  const overallCorrect =
    (hasMcq ? mcqResult!.count_correct : 0) + (cbqTaken ? correctCount : 0);
  const overallTotal =
    (hasMcq ? mcqTotalQ : 0) + (cbqTaken ? questions.length : 0);

  const goLecture = () => (backTo ? navigate(backTo) : navigate(-1));

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const tabBtn = (value: Tab, label: string) => (
    <button
      className={`transition-all duration-300 h-12 md:h-[70px] w-full rounded-md font-medium text-lg md:text-xl ${
        currentTab === value
          ? "bg-white shadow border border-black/15 text-primary"
          : ""
      }`}
      onClick={() => setCurrentTab(value)}
    >
      {label}
    </button>
  );

  const mcqTabBtn = (value: McqTab, label: string) => (
    <button
      className={`transition-all duration-300 h-12 md:h-[70px] w-full rounded-md font-medium text-lg md:text-xl ${
        mcqTab === value
          ? "bg-white shadow border border-black/15 text-primary"
          : ""
      }`}
      onClick={() => setMcqTab(value)}
    >
      {label}
    </button>
  );

  // The MCQ result section, mirroring the CBQ layout: summary, sub-tabs, list.
  const mcqPanel = mcqResult ? (
    <>
      <div className="w-full bg-[#F2F4F7] md:py-2.5 py-1.5 flex gap-4 rounded-lg border-[1.5px] border-black/5 px-2.5 mt-1">
        {mcqTabBtn("wrong", "Wrong")}
        {mcqTabBtn("correct", "Correct")}
        {mcqTabBtn("marked", "Marked")}
      </div>

      <div className="mt-8 md:space-y-28 space-y-12">
        {!mcqQuestions || mcqQuestions.length === 0 ? (
          <p className="text-center text-lightGray py-8">
            No questions in this tab.
          </p>
        ) : (
          mcqQuestions.map((q, index) => (
            <div key={index}>
              <h1 className="md:mb-10 mb-5 font-bold text-lg md:text-2xl">
                <ReactMarkdown
                  className="text-justify"
                  rehypePlugins={[rehypeRaw]}
                >
                  {q.question}
                </ReactMarkdown>
              </h1>
              <div className="md:space-y-12 space-y-6 font-bold sm:text-base text-sm md:text-2xl w-full">
                {q.choices.map((choice, choiceIndex) =>
                  choice.isCorrect ? (
                    <h1
                      key={choiceIndex}
                      className="border-[#22BB64] border rounded-xl items-center gap-2 bg-[#EFFEF4] text-[#22BB64] flex w-fit sm:py-3 py-1.5 sm:px-4 px-3"
                    >
                      {choice.answer}
                      <div className="min-w-4 min-h-4">
                        <CheckSignIcon />
                      </div>
                    </h1>
                  ) : choice.selected ? (
                    <h1
                      key={choiceIndex}
                      className="border-[#D80B0B] border rounded-xl items-center gap-2 bg-[#FFF0F0] text-[#D80B0B] flex w-fit sm:py-3 py-1.5 sm:px-4 px-3"
                    >
                      {choice.answer}
                      <div className="min-w-4 min-h-4">
                        <WrongSignIcon />
                      </div>
                    </h1>
                  ) : (
                    <h1
                      key={choiceIndex}
                      className="border border-gray-300 rounded-xl items-center gap-2 bg-white text-gray-500 flex w-fit sm:py-3 py-1.5 sm:px-4 px-3"
                    >
                      {choice.answer}
                    </h1>
                  )
                )}
              </div>
              {q.explanation ? (
                <h1 className="mt-10 font-semibold sm:text-base text-sm">
                  Explanation:{" "}
                  <span className="text-primary whitespace-pre-line">
                    {q.explanation}
                  </span>
                </h1>
              ) : null}
            </div>
          ))
        )}
      </div>
    </>
  ) : null;

  return (
    <>
      <Navbar />
      <div id="table" className="bg-white w-full min-h-screen h-full py-28">
        <div className="container">
          {/* One overall total for the whole result, shown at the top. */}
          {showOverall && (
            <div className="sm:w-[472px] mt-24 md:mt-32 py-[18px] px-6 mb-4 rounded-xl border border-black/10 bg-[#F8FAFC]">
              <p className="mb-4 font-bold md:text-2xl text-lg text-primary">
                Overall Result
              </p>
              <div className="flex gap-8 justify-between font-bold md:text-2xl text-lg">
                <div>
                  <p className="mb-2.5 sm:mb-5">Percentage</p>
                  <p>Correct</p>
                </div>
                <div className="text-right">
                  <p className="text-primary mb-2.5 sm:mb-5">
                    {overallPercentage}%
                  </p>
                  <p>
                    {overallCorrect} / {overallTotal}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* One continuous exam: MCQ answers first, CBQ answers under them. */}
          {mcqResult && (
            <div className="mb-16 md:mb-24">
              <h2 className="mb-6 font-bold text-xl md:text-3xl">MCQ Answers</h2>
              {mcqPanel}
            </div>
          )}

          {!cbqTaken && !mcqResult ? (
            <p className="text-center text-lightGray">Result not available.</p>
          ) : cbqTaken ? (
            <>
              {mcqResult && (
                <h2 className="mb-6 font-bold text-xl md:text-3xl">
                  CBQ Answers
                </h2>
              )}
              {/* Tabs */}
              <div className="w-full bg-[#F2F4F7] md:py-2.5 py-1.5 flex gap-4 rounded-lg border-[1.5px] border-black/5 px-2.5 mt-1">
                {tabBtn("all", "All")}
                {tabBtn("correct", "Correct")}
                {tabBtn("wrong", "Wrong")}
                {tabBtn("flagged", "Flagged")}
              </div>

              {/* Questions */}
              <div className="mt-8 md:space-y-28 space-y-12">
                {filtered.length === 0 ? (
                  <p className="text-center text-lightGray py-8">
                    No questions in this tab.
                  </p>
                ) : (
                  filtered.map((q, index) => (
                    <div key={q.id ?? index}>
                      <p className="mb-2 text-xs font-semibold text-lightGray">
                        Scenario {q.scenarioIndex + 1}
                        {q.scenarioTitle ? ` · ${q.scenarioTitle}` : ""}
                      </p>

                      <h1 className="md:mb-10 mb-5 font-bold text-lg md:text-2xl flex items-start gap-2">
                        <span className="text-justify">
                          {q.type === "dropdown"
                            ? fillDropdownPrompt(q.prompt, q.answerJson)
                            : q.prompt}
                        </span>
                        {q.isFlagged && (
                          <span className="text-[#eab326] shrink-0 mt-1">
                            <FlagIcon width={16} height={16} />
                          </span>
                        )}
                      </h1>

                      {/* Student answer as a colored pill (green=correct, red=wrong) */}
                      {q.type !== "dropdown" && (
                        <div className="font-bold sm:text-base text-sm md:text-2xl">
                          {q.isCorrect ? (
                            <h1 className="border-[#22BB64] border rounded-xl items-center gap-2 bg-[#EFFEF4] text-[#22BB64] flex w-fit sm:py-3 py-1.5 sm:px-4 px-3">
                              {formatAnswer(q.type, q.answerJson)}
                              <div className="min-w-4 min-h-4">
                                <CheckSignIcon />
                              </div>
                            </h1>
                          ) : (
                            <h1 className="border-[#D80B0B] border rounded-xl items-center gap-2 bg-[#FFF0F0] text-[#D80B0B] flex w-fit sm:py-3 py-1.5 sm:px-4 px-3">
                              {formatAnswer(q.type, q.answerJson)}
                              <div className="min-w-4 min-h-4">
                                <WrongSignIcon />
                              </div>
                            </h1>
                          )}
                        </div>
                      )}

                      {/* Correct answer (all types) — shown only when the
                          student's answer was wrong, styled green like the MCQ. */}
                      {!q.isCorrect && q.correctAnswerJson && (
                        <div className="mt-3 font-bold sm:text-base text-sm md:text-2xl">
                          <p className="mb-1 text-xs font-semibold text-lightGray">
                            Correct answer
                          </p>
                          <h1 className="border-[#22BB64] border rounded-xl items-center gap-2 bg-[#EFFEF4] text-[#22BB64] flex w-fit sm:py-3 py-1.5 sm:px-4 px-3">
                            {q.type === "dropdown"
                              ? fillDropdownPrompt(
                                  q.prompt,
                                  normalizeCorrect(q.type, q.correctAnswerJson)
                                )
                              : formatAnswer(
                                  q.type,
                                  normalizeCorrect(q.type, q.correctAnswerJson)
                                )}
                            <div className="min-w-4 min-h-4">
                              <CheckSignIcon />
                            </div>
                          </h1>
                        </div>
                      )}

                      {q.helpText ? (
                        <h1 className="mt-10 font-semibold sm:text-base text-sm">
                          Explanation:{" "}
                          <span className="text-primary whitespace-pre-line">
                            {q.helpText}
                          </span>
                        </h1>
                      ) : null}
                    </div>
                  ))
                )}
              </div>
            </>
          ) : null}

          <Button
            onClick={goLecture}
            variant="primary"
            className="w-full !h-12 !text-base font-bold mt-16"
          >
            Back to Lecture
          </Button>
        </div>
      </div>
    </>
  );
};

export default CbqResult;
